import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TakeittoIcon } from '../components/TakeittoLogo';
import LoginModal from '../components/LoginModal';
import {
  Truck, Bike, Store, MapPin, Zap, Camera, BarChart3, Route as RouteIcon,
  ArrowRight, Menu, X, Clock, ShieldCheck, Wallet, PackageCheck, Search,
  CheckCircle2
} from 'lucide-react';

const IMAGES = {
  heroCourier: 'https://static.prod-images.emergentagent.com/jobs/74676e9c-2951-4394-9cf6-40006386de9c/images/f14728b706b1fbf4293b958e0a59938591f83f14f551d2811033b510e9653e4d.jpeg',
  driver: 'https://images.unsplash.com/photo-1661983228855-d3fb2b3c07b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxkZWxpdmVyeSUyMGRyaXZlciUyMGhvbGRpbmclMjBwYWNrYWdlJTIwc21pbGluZ3xlbnwwfHx8fDE3ODI1NjM0Mjh8MA&ixlib=rb-4.1.0&q=85',
  merchant: 'https://images.unsplash.com/photo-1770013413878-2530e2c3d82b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBzaG9wJTIwcGFja2luZyUyMG9yZGVyfGVufDB8fHx8MTc5MzE5NDkwNnww&ixlib=rb-4.1.0&q=85',
  fleet: 'https://images.unsplash.com/photo-1587813369290-091c9d432daf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHx3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjBkZWxpdmVyeSUyMHZhbnN8ZW58MHx8fHwxNzkzMTk0OTA3fDA&ixlib=rb-4.1.0&q=85',
};

// Lightweight scroll-reveal hook
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

const NAV_LINKS = [
  { label: 'Drivers', href: '#drivers' },
  { label: 'Businesses', href: '#merchants' },
  { label: 'Features', href: '#features' },
  { label: 'How it works', href: '#how' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [trackCode, setTrackCode] = useState('');
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  useReveal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleTrack = (e) => {
    e.preventDefault();
    const code = trackCode.trim();
    if (code) navigate(`/track/${encodeURIComponent(code)}`);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" data-testid="landing-page">
      {/* ===================== HEADER ===================== */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? 'backdrop-blur-xl bg-white/85 border-b border-slate-200/70 shadow-sm' : 'bg-transparent'
        }`}
        data-testid="landing-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2.5 group"
            data-testid="nav-logo"
          >
            <TakeittoIcon size={38} className="transition-transform duration-300 group-hover:scale-110" />
            <div className="flex flex-col leading-none text-left">
              <span className={`font-extrabold text-lg tracking-tight ${scrolled ? 'text-slate-900' : 'text-white'}`}>
                Takeitto
              </span>
              <span className={`text-[10px] uppercase tracking-[0.18em] ${scrolled ? 'text-slate-400' : 'text-white/60'}`}>
                Logistics
              </span>
            </div>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className={`text-sm font-medium transition-colors ${
                  scrolled ? 'text-slate-600 hover:text-indigo-600' : 'text-white/80 hover:text-white'
                }`}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => setLoginOpen(true)}
              className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors ${
                scrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'
              }`}
              data-testid="nav-login-btn"
            >
              Log In
            </button>
            <button
              onClick={() => setSignupOpen(true)}
              className="text-sm font-semibold px-5 py-2.5 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 text-white shadow-lg shadow-indigo-600/25 hover:shadow-indigo-600/40 hover:-translate-y-0.5 transition-all duration-200"
              data-testid="nav-signup-btn"
            >
              Sign Up
            </button>
          </div>

          <button
            onClick={() => setMenuOpen((v) => !v)}
            className={`md:hidden p-2 rounded-lg ${scrolled ? 'text-slate-800' : 'text-white'}`}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 px-4 py-4 space-y-1 shadow-xl" data-testid="mobile-menu">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                className="block px-3 py-2.5 rounded-lg text-slate-700 font-medium hover:bg-slate-50"
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {l.label}
              </a>
            ))}
            <div className="pt-2 grid grid-cols-3 gap-2">
              <button
                onClick={() => { setMenuOpen(false); setLoginOpen(true); }}
                className="px-3 py-2.5 rounded-xl border border-slate-200 font-semibold text-slate-700 text-sm"
                data-testid="mobile-login-btn"
              >
                Log In
              </button>
              <button
                onClick={() => { setMenuOpen(false); navigate('/driver/signup'); }}
                className="px-3 py-2.5 rounded-xl bg-slate-900 text-white font-semibold text-sm"
                data-testid="mobile-signup-driver-btn"
              >
                Drive
              </button>
              <button
                onClick={() => { setMenuOpen(false); navigate('/onboard'); }}
                className="px-3 py-2.5 rounded-xl bg-indigo-600 text-white font-semibold text-sm"
                data-testid="mobile-signup-business-btn"
              >
                Business
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#0B0F17] text-white pt-28 pb-20 md:pt-36 md:pb-28">
        {/* ambient glows */}
        <div className="absolute -top-32 -left-24 w-[32rem] h-[32rem] bg-indigo-600/30 rounded-full blur-[120px] lp-float-slow" />
        <div className="absolute top-20 right-0 w-[26rem] h-[26rem] bg-violet-600/25 rounded-full blur-[120px] lp-float" />
        <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-cyan-400/10 rounded-full blur-[100px]" />
        {/* grid pattern */}
        <div className="absolute inset-0 opacity-[0.07]">
          <svg className="w-full h-full" preserveAspectRatio="none">
            <defs>
              <pattern id="hgrid" width="48" height="48" patternUnits="userSpaceOnUse">
                <path d="M 48 0 L 0 0 0 48" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hgrid)" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold tracking-[0.15em] text-cyan-300">
              <Zap size={14} className="fill-cyan-300 text-cyan-300" /> NEXT-GEN LAST-MILE LOGISTICS
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Smarter routes.<br />
              <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-emerald-300 bg-clip-text text-transparent">
                Faster deliveries.
              </span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              The AI-powered delivery network connecting independent drivers with ambitious
              businesses — real-time tracking, intelligent route optimization and automated
              dispatch, all in one platform.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/driver/signup')}
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-white text-slate-900 font-bold text-base shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                data-testid="hero-signup-driver-btn"
              >
                <Bike size={20} /> Sign up to deliver
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => navigate('/onboard')}
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-violet-500 text-white font-bold text-base shadow-xl shadow-purple-900/40 hover:-translate-y-0.5 transition-all duration-200"
                data-testid="hero-signup-business-btn"
              >
                <Store size={20} /> Sign up your business
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            {/* Quick track */}
            <form onSubmit={handleTrack} className="mt-8 max-w-md" data-testid="hero-track-form">
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-white/50">
                Track a delivery
              </label>
              <div className="mt-2 flex items-center gap-2 bg-white/10 border border-white/15 rounded-xl p-1.5 focus-within:border-cyan-300/60 transition-colors">
                <Search size={18} className="ml-2 text-white/50 shrink-0" />
                <input
                  value={trackCode}
                  onChange={(e) => setTrackCode(e.target.value)}
                  placeholder="Enter tracking code (e.g. TRK-88219)"
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/40 text-white"
                  data-testid="hero-track-input"
                />
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-cyan-400 text-slate-900 font-semibold text-sm hover:bg-cyan-300 transition-colors"
                  data-testid="hero-track-btn"
                >
                  Track
                </button>
              </div>
            </form>
          </div>

          {/* Hero visual */}
          <div className="relative reveal">
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-white/10">
              <img
                src={IMAGES.heroCourier}
                alt="Takeitto courier delivering in the city"
                className="w-full h-[26rem] lg:h-[32rem] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/70 via-transparent to-transparent" />
            </div>

            {/* Floating stat card: live tracking */}
            <div className="absolute -left-4 top-10 bg-white rounded-2xl shadow-2xl p-4 w-52 lp-float">
              <div className="flex items-center gap-2 text-emerald-600">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <span className="text-xs font-bold uppercase tracking-wider">Live</span>
              </div>
              <p className="mt-2 text-sm font-bold text-slate-900">On the way</p>
              <p className="text-xs text-slate-500">ETA 12 min · 2.4 km away</p>
            </div>

            {/* Floating card: live tracking */}
            <div className="absolute -right-3 bottom-8 bg-white rounded-2xl shadow-2xl p-4 w-44 lp-float-slow">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <RouteIcon size={18} className="text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-extrabold text-slate-900 leading-tight">Live tracking</p>
                  <p className="text-[11px] text-slate-500">on every order</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== TRUST MARQUEE ===================== */}
      <div className="bg-slate-50 border-y border-slate-200/70 py-6 overflow-hidden">
        <div className="flex items-center gap-14 whitespace-nowrap lp-marquee w-max text-slate-400">
          {[...Array(2)].map((_, dup) =>
            ['Real-time GPS', 'AI route engine', 'Proof of delivery', 'Instant payouts', 'API & POS sync', 'Live customer links', 'Automated dispatch'].map((t, i) => (
              <span key={`${dup}-${i}`} className="flex items-center gap-3 text-sm font-semibold uppercase tracking-wider">
                <CheckCircle2 size={16} className="text-indigo-400" /> {t}
              </span>
            ))
          )}
        </div>
      </div>

      {/* ===================== DUAL PATHWAY ===================== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl reveal">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-indigo-500">Two ways to grow with Takeitto</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">
              Whether you deliver or you sell, we move it for you.
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {/* Driver card */}
            <div
              id="drivers"
              className="reveal group relative rounded-3xl overflow-hidden bg-slate-900 text-white scroll-mt-24"
              data-testid="pathway-driver-card"
            >
              <img src={IMAGES.driver} alt="Takeitto delivery driver" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-900/40" />
              <div className="relative p-8 lg:p-10 flex flex-col h-full min-h-[30rem]">
                <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-400/20 text-cyan-300 text-xs font-bold tracking-wide">
                  <Wallet size={13} /> EARN ON YOUR SCHEDULE
                </span>
                <h3 className="mt-5 text-2xl sm:text-3xl font-extrabold">Drive & earn with Takeitto</h3>
                <p className="mt-3 text-white/70 max-w-sm">
                  Flexible hours, smart route batching and transparent pay. Bring your bike, car or van.
                </p>
                <ul className="mt-6 space-y-3 text-sm">
                  {[
                    { icon: Wallet, t: 'Fast, transparent payouts' },
                    { icon: RouteIcon, t: 'AI routing cuts empty miles' },
                    { icon: Clock, t: 'Work when it suits you' },
                  ].map((p, i) => (
                    <li key={i} className="flex items-center gap-3 text-white/85">
                      <span className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                        <p.icon size={15} className="text-cyan-300" />
                      </span>
                      {p.t}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/driver/signup')}
                  className="mt-auto pt-8 group/btn inline-flex items-center gap-2 text-base font-bold text-white"
                  data-testid="pathway-driver-signup-btn"
                >
                  <span className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-slate-900 shadow-lg group-hover/btn:-translate-y-0.5 transition-transform">
                    Become a driver <ArrowRight size={18} />
                  </span>
                </button>
              </div>
            </div>

            {/* Merchant card */}
            <div
              id="merchants"
              className="reveal group relative rounded-3xl overflow-hidden bg-indigo-700 text-white scroll-mt-24"
              data-testid="pathway-merchant-card"
            >
              <img src={IMAGES.merchant} alt="Takeitto merchant partner" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-950 via-indigo-900/85 to-purple-800/50" />
              <div className="relative p-8 lg:p-10 flex flex-col h-full min-h-[30rem]">
                <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-400/25 text-emerald-200 text-xs font-bold tracking-wide">
                  <PackageCheck size={13} /> SHIP WITH CONFIDENCE
                </span>
                <h3 className="mt-5 text-2xl sm:text-3xl font-extrabold">Ship with Takeitto</h3>
                <p className="mt-3 text-white/75 max-w-sm">
                  Plug your store in and dispatch local deliveries in under a minute — with live tracking your customers will love.
                </p>
                <ul className="mt-6 space-y-3 text-sm">
                  {[
                    { icon: Zap, t: 'Automated dispatch in seconds' },
                    { icon: MapPin, t: 'Live GPS links for customers' },
                    { icon: BarChart3, t: 'Dispatch & delivery analytics' },
                  ].map((p, i) => (
                    <li key={i} className="flex items-center gap-3 text-white/90">
                      <span className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                        <p.icon size={15} className="text-emerald-200" />
                      </span>
                      {p.t}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/onboard')}
                  className="mt-auto pt-8 group/btn inline-flex items-center gap-2 text-base font-bold"
                  data-testid="pathway-merchant-signup-btn"
                >
                  <span className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-indigo-700 shadow-lg group-hover/btn:-translate-y-0.5 transition-transform">
                    Register your business <ArrowRight size={18} />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FEATURES ===================== */}
      <section id="features" className="py-24 bg-slate-50 border-y border-slate-200/70 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl reveal">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-indigo-500">Built for precision</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">
              Everything you need to move the last mile.
            </h2>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: RouteIcon, title: 'AI route engine', desc: 'Dynamic routing re-optimizes around traffic and new orders in real time.', bg: 'bg-indigo-50', fg: 'text-indigo-600' },
              { icon: MapPin, title: 'Live GPS tracking', desc: 'Sub-second location updates with shareable links for your customers.', bg: 'bg-cyan-50', fg: 'text-cyan-600' },
              { icon: Camera, title: 'Proof of delivery', desc: 'Photo capture and signature logged against every completed drop.', bg: 'bg-emerald-50', fg: 'text-emerald-600' },
              { icon: BarChart3, title: 'Command center', desc: 'A dispatch dashboard with analytics across your whole fleet.', bg: 'bg-violet-50', fg: 'text-violet-600' },
            ].map((f, i) => (
              <div
                key={i}
                className="reveal group bg-white rounded-2xl p-6 border border-slate-200/80 hover:border-indigo-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                style={{ transitionDelay: `${i * 60}ms` }}
                data-testid={`feature-card-${i}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${f.bg} group-hover:scale-110 transition-transform`}>
                  <f.icon size={22} className={f.fg} />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== HOW IT WORKS ===================== */}
      <section id="how" className="py-24 bg-[#0B0F17] text-white relative overflow-hidden scroll-mt-20">
        <div className="absolute top-10 right-10 w-96 h-96 bg-indigo-600/20 rounded-full blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-cyan-300 reveal">How it works</p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight reveal">
                From checkout to doorstep in three steps.
              </h2>
              <div className="mt-10 space-y-8">
                {[
                  { n: '01', t: 'Business dispatches an order', d: 'Orders flow in from your store or POS and land in the Takeitto dispatch center instantly.' },
                  { n: '02', t: 'AI assigns the best driver & route', d: 'Our engine matches the nearest available driver and plots the fastest multi-stop route.' },
                  { n: '03', t: 'Customer tracks to the door', d: 'Live GPS, ETA updates and photo proof of delivery — all shared automatically.' },
                ].map((s, i) => (
                  <div key={i} className="reveal flex gap-5" style={{ transitionDelay: `${i * 80}ms` }}>
                    <span className="text-2xl font-extrabold bg-gradient-to-b from-cyan-300 to-indigo-400 bg-clip-text text-transparent shrink-0 w-12">
                      {s.n}
                    </span>
                    <div>
                      <h3 className="text-lg font-bold">{s.t}</h3>
                      <p className="mt-1.5 text-white/60 leading-relaxed">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="reveal relative">
              <div className="rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-white/10">
                <img src={IMAGES.fleet} alt="Takeitto delivery fleet" className="w-full h-[28rem] object-cover" />
              </div>
              <div className="absolute -bottom-5 left-6 right-6 bg-white/95 backdrop-blur rounded-2xl p-5 shadow-2xl flex items-center justify-between">
                <div className="flex items-center gap-3 text-slate-900">
                  <ShieldCheck size={26} className="text-emerald-500" />
                  <div>
                    <p className="font-bold text-sm">Delivered & verified</p>
                    <p className="text-xs text-slate-500">Photo proof captured</p>
                  </div>
                </div>
                <span className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">COMPLETE</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto reveal relative rounded-[2.5rem] overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 p-10 sm:p-16 text-white shadow-2xl">
          <div className="absolute -top-20 -right-10 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 left-10 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl" />
          <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight max-w-lg">
                Ready to move faster with Takeitto?
              </h2>
              <p className="mt-3 text-white/80 max-w-md">
                Join the network in minutes. No setup fees — start delivering or dispatching today.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <button
                onClick={() => navigate('/driver/signup')}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-white text-indigo-700 font-bold shadow-xl hover:-translate-y-0.5 transition-transform"
                data-testid="cta-driver-signup-btn"
              >
                <Bike size={18} /> Sign up to deliver
              </button>
              <button
                onClick={() => navigate('/onboard')}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-slate-900/40 border border-white/30 text-white font-bold hover:bg-slate-900/60 transition-colors"
                data-testid="cta-merchant-signup-btn"
              >
                <Store size={18} /> Sign up your business
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FOOTER ===================== */}
      <footer className="bg-slate-950 text-slate-400 pt-16 pb-10" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5">
                <TakeittoIcon size={36} />
                <span className="font-extrabold text-lg text-white">Takeitto</span>
              </div>
              <p className="mt-4 text-sm max-w-xs leading-relaxed">
                AI-powered last-mile logistics — smarter routes, faster deliveries, real-time precision.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Drivers</h4>
              <ul className="space-y-2.5 text-sm">
                <li><button onClick={() => navigate('/driver/signup')} className="hover:text-white transition-colors" data-testid="footer-driver-signup">Become a driver</button></li>
                <li><button onClick={() => setLoginOpen(true)} className="hover:text-white transition-colors" data-testid="footer-driver-login">Driver login</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Businesses</h4>
              <ul className="space-y-2.5 text-sm">
                <li><button onClick={() => navigate('/onboard')} className="hover:text-white transition-colors" data-testid="footer-merchant-signup">Register business</button></li>
                <li><button onClick={() => setLoginOpen(true)} className="hover:text-white transition-colors" data-testid="footer-merchant-login">Business login</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Platform</h4>
              <ul className="space-y-2.5 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#how" className="hover:text-white transition-colors">How it works</a></li>
                <li><button onClick={() => navigate('/demo')} className="hover:text-white transition-colors" data-testid="footer-demo">View demo</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
            <p>© {new Date().getFullYear()} Takeitto. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <span className="hover:text-white transition-colors cursor-default">Privacy</span>
              <span className="hover:text-white transition-colors cursor-default">Terms</span>
            </div>
          </div>
        </div>
      </footer>

      {/* ===================== SIGN UP CHOICE MODAL ===================== */}
      {signupOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-fade-in"
          onClick={() => setSignupOpen(false)}
          data-testid="signup-choice-modal"
        >
          <div
            className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl p-7 sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSignupOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              data-testid="signup-choice-close"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <h3 className="text-2xl font-extrabold tracking-tight text-slate-900">Join Takeitto</h3>
            <p className="mt-1.5 text-sm text-slate-500">How would you like to sign up?</p>

            <div className="mt-6 space-y-3">
              <button
                onClick={() => { setSignupOpen(false); navigate('/driver/signup'); }}
                className="group w-full flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 transition-all text-left"
                data-testid="signup-choice-driver-btn"
              >
                <span className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
                  <Bike size={22} className="text-cyan-300" />
                </span>
                <span className="flex-1">
                  <span className="block font-bold text-slate-900">Sign up as a Driver</span>
                  <span className="block text-sm text-slate-500">Deliver on your schedule and earn</span>
                </span>
                <ArrowRight size={18} className="text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" />
              </button>

              <button
                onClick={() => { setSignupOpen(false); navigate('/onboard'); }}
                className="group w-full flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 transition-all text-left"
                data-testid="signup-choice-merchant-btn"
              >
                <span className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0">
                  <Store size={22} className="text-white" />
                </span>
                <span className="flex-1">
                  <span className="block font-bold text-slate-900">Sign up as a Merchant</span>
                  <span className="block text-sm text-slate-500">Ship orders with live tracking</span>
                </span>
                <ArrowRight size={18} className="text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" />
              </button>
            </div>

            <p className="mt-6 text-center text-sm text-slate-500">
              Already have an account?{' '}
              <button
                onClick={() => { setSignupOpen(false); setLoginOpen(true); }}
                className="font-semibold text-indigo-600 hover:underline"
                data-testid="signup-choice-login-link"
              >
                Log in
              </button>
            </p>
          </div>
        </div>
      )}
      {/* ===================== LOGIN MODAL ===================== */}
      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSignupClick={() => setSignupOpen(true)}
      />
    </div>
  );
}
